// Microbe RTS — world construction + shared low-level helpers.
//
// This is the base module of the sim layer: it owns the seeded RNG, tiny vec math, node/unit
// lookups, and it builds the initial GameState from config's NODE_LAYOUT. Node capture logic
// (claim neutrals / erode enemy nodes) lives here too since it is purely node bookkeeping.
//
// It imports NOTHING from the other sim modules (units/combat/economy/…), so there is no cycle:
// everyone else imports from here.

import type {
  GameState, WorldNode, Unit, Colony, Side, Alliance, UnitKind, Vec, NodeType, GameEvent,
} from '../types.js';
import {
  WORLD, NODE_GEN, NODE_SPECS, START, POP, CAPTURE, CLOCKS, BOT, WILD,
  HEALER, GRAZER, UNITS, DEFAULT_BAND_STANCE, QUEEN, SEED_COST, COMBAT,
} from '../config.js';
import type { UnitSpec, Difficulty, NodeGenType, BotProfile } from '../config.js';

// ---------------------------------------------------------------------------
// Shared context threaded through the tick (deterministic RNG + difficulty).
// ---------------------------------------------------------------------------
export interface SimCtx {
  rng: () => number;
  difficulty: Difficulty;
  // Per-side resolved bot profile (income + expansion). null = human-driven side (no scaling).
  // Sandbox sets both; the real game sets only 'bot'. Consumed by economy for per-side income.
  profiles: Record<Side, BotProfile | null>;
}

/** Deterministic PRNG (mulberry32). NEVER use Math.random in this layer. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------------------
// Tiny math + lookups.
// ---------------------------------------------------------------------------
export function clampNum(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
export function dist(a: Vec, b: Vec): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
export function dist2(a: Vec, b: Vec): number {
  const dx = a.x - b.x, dy = a.y - b.y;
  return dx * dx + dy * dy;
}
/** Normalized depth (0 surface .. 1 floor) at a world position. */
export function depthAt(pos: Vec): number {
  return clampNum(pos.y / WORLD.height, 0, 1);
}

export function unitSpec(kind: UnitKind): UnitSpec {
  if (kind === 'healer') return HEALER;
  if (kind === 'grazer') return GRAZER;
  return UNITS[kind]; // WarriorClass (incl. drone)
}
/** True for kinds that never deal damage (healer only). */
export function isNonCombat(kind: UnitKind): boolean {
  return kind === 'healer';
}
/** A queen's live bite (drone base × queen multiplier). Melee only — she never casts DoT. */
export function queenDmg(u: Unit): number {
  return unitSpec(u.kind).dmg * (u.queen ? QUEEN.dmgMult : 1);
}
/** A unit's live move speed BEFORE genome mults (applies the queen's slight slow). */
export function baseSpeedOf(u: Unit): number {
  return unitSpec(u.kind).speed * (u.queen ? QUEEN.speedMult : 1);
}
/** Promote a drone to Queen in place: bigger, tankier, self-mending (dmg/speed applied at read-time). */
export function promoteToQueen(u: Unit): void {
  if (u.queen) return;
  u.queen = true;
  u.radius = u.radius * QUEEN.radiusMult;
  u.maxHp = u.maxHp * QUEEN.hpMult;
  u.hp = u.maxHp;
}
/** Effective attack DPS of a unit kind (used for node-capture erosion). */
export function unitDps(kind: UnitKind): number {
  const s = unitSpec(kind);
  return s.dmg / Math.max(0.0001, s.interval);
}

export function nodeById(state: GameState, id: string): WorldNode | undefined {
  for (const n of state.nodes) if (n.id === id) return n;
  return undefined;
}
export function unitById(state: GameState, id: string): Unit | undefined {
  for (const u of state.units) if (u.id === id) return u;
  return undefined;
}
export function other(side: Side): Side {
  return side === 'you' ? 'bot' : 'you';
}

/** Owned non-Home (capturable) nodes for a side. */
export function ownedCapturable(state: GameState, side: Side): WorldNode[] {
  const out: WorldNode[] = [];
  for (const n of state.nodes) if (n.owner === side && n.type !== 'home') out.push(n);
  return out;
}
export function ownsNodeType(state: GameState, side: Side, type: NodeType): boolean {
  for (const n of state.nodes) if (n.owner === side && n.type === type) return true;
  return false;
}
export function countOwnedType(state: GameState, side: Side, type: NodeType): number {
  let c = 0;
  for (const n of state.nodes) if (n.owner === side && n.type === type) c++;
  return c;
}

// ---------------------------------------------------------------------------
// Unit factory (base stats, no genome buffs — units.ts wraps this with mults).
// ---------------------------------------------------------------------------
export function makeUnit(
  state: GameState,
  side: Alliance,
  kind: UnitKind,
  pos: Vec,
  buildIndex = 0,
): Unit {
  const s = unitSpec(kind);
  const u: Unit = {
    id: `u${state.nextId++}`,
    side,
    kind,
    queen: false,
    pos: { x: pos.x, y: pos.y },
    vel: { x: 0, y: 0 },
    hp: s.hp,
    maxHp: s.hp,
    radius: s.radius,
    cooldown: 0,
    moveTarget: null,
    moveSpeedCap: null,
    focusId: null,
    attackNodeId: null,
    assignedNode: null,
    stance: 'photo',
    bandStance: DEFAULT_BAND_STANCE,
    dotStacks: 0,
    dotTimer: 0,
    dotSide: null,
    poisonTimer: 0,
    poisonDose: 0,
    poisonStacks: 0,
    poisonDps: 0,
    buildIndex,
    guardPos: null,
    provoke: 0,
  };
  state.units.push(u);
  return u;
}

// ---------------------------------------------------------------------------
// Initial state.
// ---------------------------------------------------------------------------
/** Add a node to the state from a normalized (x, depth) placement. Returns the node. */
function pushNode(state: GameState, type: NodeType, xFrac: number, depth: number): WorldNode {
  const spec = NODE_SPECS[type];
  const pos: Vec = { x: xFrac * WORLD.width, y: depth * WORLD.height };
  const towardCenter = Math.sign(WORLD.width / 2 - pos.x) || 1;
  const rally: Vec =
    type === 'home'
      ? { x: clampNum(pos.x + towardCenter * 150, 0, WORLD.width), y: pos.y + 120 }
      : { x: pos.x, y: pos.y };
  const node: WorldNode = {
    id: `nd${state.nodes.length}`,
    type,
    pos,
    depth,
    owner: 'neutral', // EVERYTHING starts neutral now — homes are colonized, not granted
    integrity: spec.integrity,
    maxIntegrity: spec.integrity,
    workerCap: spec.workerCap,
    assigned: [],
    claimProgress: 0,
    contested: false,
    rally,
    hasBrood: false, // a colonized home flips this true on establish
    radius: spec.radius,
    fireCooldown: 0,
  };
  state.nodes.push(node);
  return node;
}

function rnd(rng: () => number, lo: number, hi: number): number {
  return lo + (hi - lo) * rng();
}

/** Reject-sample a node position within a depth band that isn't too close to existing nodes. */
function placeNode(state: GameState, gen: NodeGenType, rng: () => number): void {
  const minSep = NODE_GEN.minSepFrac * WORLD.height;
  for (let c = 0; c < gen.count; c++) {
    let best: Vec | null = null;
    let bestClearance = -1;
    // Try several candidates; keep the one most separated from existing nodes (graceful even
    // when the band is crowded — never fails to place, just picks the roomiest slot).
    for (let attempt = 0; attempt < 24; attempt++) {
      const x = rnd(rng, NODE_GEN.xRange[0], NODE_GEN.xRange[1]);
      const d = rnd(rng, gen.depth[0], gen.depth[1]);
      const p: Vec = { x: x * WORLD.width, y: d * WORLD.height };
      let clearance = Infinity;
      for (const n of state.nodes) clearance = Math.min(clearance, dist(p, n.pos));
      if (clearance >= minSep) { best = p; break; }
      if (clearance > bestClearance) { bestClearance = clearance; best = p; }
    }
    if (best) pushNode(state, gen.type, best.x / WORLD.width, best.y / WORLD.height);
  }
}

export function buildInitialState(seed = 1, difficulty: Difficulty = 'reef'): GameState {
  const s = (seed >>> 0) || 1;
  const rng = mulberry32(s ^ 0x1b56c4e9);

  const state: GameState = {
    nodes: [],
    units: [],
    lairs: [],
    colonies: {
      you: newColony('you'),
      bot: newColony('bot'),
    },
    clocks: {
      sim: 0,
      paused: false,
      oxyActive: false,
      oxyLevel: 0,
      botThreatTimer: CLOCKS.botFirstPushAt + BOT[difficulty].threatClockOffset,
      botWave: 0,
      domYou: 0,
      domBot: 0,
    },
    result: { over: false, winner: null, reason: '' },
    selectionBySide: { you: [], bot: [] },
    groupsBySide: { you: [[], [], [], []], bot: [[], [], [], []] },
    events: [],
    shots: [],
    turrets: [],
    nextId: 1,
    seed: s,
  };

  // --- NO fixed home craters anymore: whichever crater a side settles FIRST becomes its home
  //     (retyped to a home in establishNode). The map grants nothing — you carve your capital. ---
  const H = NODE_GEN.home;

  // --- Capturable nodes: random within each band, spaced apart, lights near the surface ---
  for (const gen of NODE_GEN.capturable) placeNode(state, gen, rng);

  // --- Drop each side's opening DRONE at a RANDOM point ANYWHERE on the map, held clear of every
  //     crater (you must explore to find one to settle). The two drops are then pushed ≥40% of the
  //     map diagonal apart so the sides never spawn on top of each other. ---
  const rollDrop = (): Vec => {
    let best: Vec = {
      x: rnd(rng, H.spawnX[0], H.spawnX[1]) * WORLD.width,
      y: rnd(rng, H.spawnDepth[0], H.spawnDepth[1]) * WORLD.height,
    };
    let bestClear = -1;
    for (let attempt = 0; attempt < 40; attempt++) {
      const p: Vec = {
        x: rnd(rng, H.spawnX[0], H.spawnX[1]) * WORLD.width,
        y: rnd(rng, H.spawnDepth[0], H.spawnDepth[1]) * WORLD.height,
      };
      let clear = Infinity;
      for (const n of state.nodes) clear = Math.min(clear, dist(p, n.pos));
      if (clear >= H.minDropFromNode) return p; // far enough from every crater
      if (clear > bestClear) { bestClear = clear; best = p; } // else keep the roomiest so far
    }
    return best;
  };
  const minSpawnSep = H.minSpawnSepFrac * Math.hypot(WORLD.width, WORLD.height);
  let youC = rollDrop();
  let botC = rollDrop();
  for (let attempt = 0; attempt < 60 && dist(youC, botC) < minSpawnSep; attempt++) {
    youC = rollDrop();
    botC = rollDrop();
  }
  const drops: Record<Side, Vec> = { you: youC, bot: botC };
  for (const side of ['you', 'bot'] as Side[]) {
    const c = drops[side];
    let bi = 0;
    for (const kind of Object.keys(START.squad) as UnitKind[]) {
      const n = START.squad[kind] ?? 0;
      for (let i = 0; i < n; i++) makeUnit(state, side, kind, jitter(c, H.dropSpread, rng), bi++);
    }
  }

  // --- Establish the fixed wild LAIRS (permanent territories) + seed a grazer at each ---
  placeLairs(state, rng);

  return state;
}

/** Spawn a grazer BOUND to a fixed lair position (its guard/territory centre for the whole match). */
export function spawnGrazerAt(state: GameState, pos: Vec): Unit {
  const g = makeUnit(state, 'wild', 'grazer', { x: pos.x, y: pos.y });
  g.guardPos = { x: pos.x, y: pos.y };
  g.stance = 'photo';
  return g;
}

/**
 * Generate the match's fixed wild lairs. Each is a permanent territory anchor (grazers patrol it,
 * leash back to it when they chase, and RESPAWN there for the rest of the game). Lairs are spaced
 * from each other, the home craters, and the drop squads so nobody is ambushed on the spawn.
 */
function placeLairs(state: GameState, rng: () => number): void {
  const minClear = 900; // > territoryRadius, so nothing spawns already-aggroed onto someone
  for (let i = 0; i < WILD.count; i++) {
    let pos: Vec = { x: WORLD.width / 2, y: WORLD.height / 2 };
    let bestClear = -1;
    for (let attempt = 0; attempt < 24; attempt++) {
      const p: Vec = {
        x: rnd(rng, WILD.xRange[0], WILD.xRange[1]) * WORLD.width,
        y: rnd(rng, WILD.depth[0], WILD.depth[1]) * WORLD.height,
      };
      let clear = Infinity;
      // Space lairs from EVERY crater (not just 'home' — none exist at gen anymore): whichever crater
      // a side settles first becomes its Home, so a lair inside a crater's territory would aggro a
      // grazer onto the lone founding drone mid-seed. Clearing all craters keeps every home safe.
      for (const n of state.nodes) clear = Math.min(clear, dist(p, n.pos));
      for (const u of state.units) if (u.side !== 'wild') clear = Math.min(clear, dist(p, u.pos));
      for (const l of state.lairs) clear = Math.min(clear, dist(p, l.pos)); // space lairs apart too
      if (clear >= minClear) { pos = p; break; }
      if (clear > bestClear) { bestClear = clear; pos = p; } // fall back to the roomiest candidate
    }
    state.lairs.push({ id: `lair${i}`, pos, respawn: WILD.respawnSeconds });
    spawnGrazerAt(state, pos);
  }
}

function newColony(side: Side): Colony {
  return {
    side,
    res: { energy: START.energy, amino: START.amino, toxin: START.toxin },
    genome: {},
    keystones: [],
    tier: 1,
    homeId: '',
    popUsed: 0,
    popCap: POP.base,
  };
}

function jitter(base: Vec, spread: number, rng: () => number): Vec {
  return {
    x: clampNum(base.x + (rng() - 0.5) * spread, 0, WORLD.width),
    y: clampNum(base.y + (rng() - 0.5) * spread, 0, WORLD.height),
  };
}

// ---------------------------------------------------------------------------
// Node capture / claim (runs after economy each tick).
// ---------------------------------------------------------------------------
/** Core-hitbox radius: the small "structure" a warrior must reach to damage the base. */
function coreRadius(n: WorldNode): number {
  return n.radius * CAPTURE.coreRadiusFrac;
}
/** How close (unit center → node center) a unit must be for its WEAPON to reach the core hitbox.
 *  A base is a combat target: a MELEE unit must touch the core rim; a RANGED unit (stinger/toxifer)
 *  hits it from its full range — so it can shell the node from outside the poison ooze. */
export function siegeReach(u: Unit, n: WorldNode): number {
  const spec = unitSpec(u.kind);
  const core = coreRadius(n);
  if (spec.range > 0) {
    const lit = depthAt(u.pos) <= WORLD.bands.photic[1];
    return spec.range + (lit ? COMBAT.rangedLightBonus : 0) + core;
  }
  return u.radius + core + COMBAT.reach;
}
/** Total attack DPS a side's units are dealing to the node — anyone whose weapon can REACH the core,
 *  not just those standing inside it. Pure damage: reach is by weapon range, so a stinger erodes at
 *  distance. (Queens hit for their boosted bite.) */
function coreWarriorDps(state: GameState, side: Side, n: WorldNode): number {
  let d = 0;
  for (const u of state.units) {
    if (u.side !== side || u.hp <= 0) continue;
    if (dist(u.pos, n.pos) <= siegeReach(u, n)) d += unitDps(u.kind) * (u.queen ? QUEEN.dmgMult : 1);
  }
  return d;
}
/** Count a side's SEEDERS (drones, incl. queens) on the node's core — they found/rebuild a colony. */
function coreSeeders(state: GameState, side: Side, n: WorldNode): number {
  const core = coreRadius(n);
  let c = 0;
  for (const u of state.units) {
    if (u.side !== side || u.hp <= 0 || u.kind !== 'drone') continue;
    if (dist(u.pos, n.pos) <= core) c++;
  }
  return c;
}
/** Integrity/sec a side's drones/queens repair on a friendly owned base (anywhere in the ring, capped). */
function founderRepairRate(state: GameState, side: Side, n: WorldNode): number {
  let c = 0;
  for (const u of state.units) {
    if (u.side !== side || u.hp <= 0 || u.kind !== 'drone') continue;
    if (dist(u.pos, n.pos) <= n.radius) c++;
  }
  if (c > CAPTURE.maxRepairFounders) c = CAPTURE.maxRepairFounders;
  return c * CAPTURE.founderRepairPerSec;
}
/** The drone on a node's core to crown (a non-queen founder is promoted; else the nearest drone). */
function pickFounder(state: GameState, side: Side, n: WorldNode): Unit | null {
  const core = coreRadius(n);
  let fallback: Unit | null = null;
  for (const u of state.units) {
    if (u.side !== side || u.hp <= 0 || u.kind !== 'drone') continue;
    if (dist(u.pos, n.pos) > core) continue;
    if (!u.queen) return u; // prefer crowning a fresh drone
    fallback = u; // an already-crowned queen seeded it — nothing new to promote
  }
  return fallback;
}

/** A drone SEEDS (founds/rebuilds) a colony on a neutral crater, and is crowned Queen for it.
 *  Forward nodes start Brood-less. The seed cost (if any) is charged by the caller. */
function establishNode(state: GameState, n: WorldNode, side: Side): void {
  const founder = pickFounder(state, side, n);
  const hasHome = !!nodeById(state, state.colonies[side].homeId);
  n.owner = side;
  n.integrity = n.maxIntegrity;
  n.claimProgress = 0;
  n.contested = false;
  if (!hasHome) {
    // Your FIRST colony IS your home — whatever crater it lands on is RETYPED into a home, so it
    // inherits the home's tougher integrity, defenses, wider vision, passive trickle, Brood, and the
    // conquest win-condition. No fixed home craters exist anymore — the first crater you settle is it.
    // (This is the free seed; see seedIsFree.)
    const hspec = NODE_SPECS.home;
    n.type = 'home';
    n.workerCap = hspec.workerCap;
    n.radius = hspec.radius;
    n.maxIntegrity = hspec.integrity;
    n.integrity = hspec.integrity;
    const toward = Math.sign(WORLD.width / 2 - n.pos.x) || 1;
    n.rally = { x: clampNum(n.pos.x + toward * 150, 0, WORLD.width), y: n.pos.y + 120 };
    state.colonies[side].homeId = n.id;
    n.hasBrood = true;
  } else if (n.type === 'home' || n.id === state.colonies[side].homeId) {
    // A home crater (or re-seeding your own home node) comes with its Brood.
    n.hasBrood = true;
  } else {
    n.hasBrood = false; // a forward colony must (re)build a Brood to produce
  }
  if (founder) promoteToQueen(founder); // a successful seed crowns her (no-op if already a queen)
  pushEvent(state, { kind: 'nodeClaimed', side, pos: { ...n.pos }, at: state.clocks.sim });
  if (side === 'bot') {
    pushEvent(state, { kind: 'rivalExpanded', side: 'bot', pos: { ...n.pos }, at: state.clocks.sim });
  }
}
function loseNode(state: GameState, n: WorldNode): void {
  const old = n.owner;
  n.assigned = [];
  n.owner = 'neutral';
  n.hasBrood = false;
  n.integrity = 0;
  n.claimProgress = 0;
  n.contested = false;
  if (old === 'you' || old === 'bot') {
    pushEvent(state, { kind: 'nodeLost', side: old, pos: { ...n.pos }, at: state.clocks.sim });
  }
}

export function pushEvent(state: GameState, ev: GameEvent): void {
  state.events.push(ev);
  // Keep the queue bounded so a long unattended match can't grow it forever.
  if (state.events.length > 64) state.events.splice(0, state.events.length - 64);
}

/** Is this a FREE seed for `side`? Your FIRST colony (you have no home yet) is always free — it
 *  becomes your home. A home crater is also free. Every OTHER seed costs SEED_COST (checked
 *  against the colony's resources by the caller). */
function seedIsFree(state: GameState, side: Side, n: WorldNode): boolean {
  const hasHome = !!nodeById(state, state.colonies[side].homeId);
  return !hasHome || n.type === 'home';
}
/** Can `side` pay for (and thus complete) a seed on `n` this instant? */
function canSeed(state: GameState, side: Side, n: WorldNode): boolean {
  if (seedIsFree(state, side, n)) return true;
  const r = state.colonies[side].res;
  return r.energy >= SEED_COST.energy && r.amino >= SEED_COST.amino && r.toxin >= SEED_COST.toxin;
}
/** Charge the seed cost (no-op for a free seed). Called only once the channel completes. */
function chargeSeed(state: GameState, side: Side, n: WorldNode): void {
  if (seedIsFree(state, side, n)) return;
  const r = state.colonies[side].res;
  r.energy -= SEED_COST.energy;
  r.amino -= SEED_COST.amino;
  r.toxin -= SEED_COST.toxin;
}
/** Toast (throttled to ~once/2s) that a parked drone can't afford to seed this crater. */
function emitSeedBlocked(state: GameState, n: WorldNode, dt: number): void {
  const now = state.clocks.sim;
  if (Math.floor(now / 2) === Math.floor((now - dt) / 2)) return; // only on a 2s boundary crossing
  pushEvent(state, { kind: 'seedBlocked', side: 'you', pos: { ...n.pos }, at: now });
}

/**
 * Bases-as-combat-structures (NOT capture-the-flag).
 *
 *   Owned base: enemy units on its core erode integrity; friendly SCOUTS/QUEENS in the ring
 *     repair it. Net integrity change = repair - assault. No "defender freeze" — you hold a
 *     base by killing the attackers (combat + ooze), not by standing on it. Integrity -> 0
 *     destroys it (a Home at 0 ends the match via the conquest check in clocks.ts).
 *   Neutral crater: a lone side's DRONE channels `establishSeconds` to seed a colony. Only a
 *     drone can seed; enemy units on the core contest it. Non-Home seeds cost SEED_COST — the
 *     channel completes only when the colony can pay (otherwise it holds, full, and waits).
 */
export function updateCapture(state: GameState, dt: number): void {
  for (const n of state.nodes) {
    if (n.owner === 'you' || n.owner === 'bot') {
      const foe = other(n.owner);
      const assault = coreWarriorDps(state, foe, n);
      const repair = founderRepairRate(state, n.owner, n);
      n.contested = assault > 0;
      n.integrity = clampNum(n.integrity + (repair - assault) * dt, 0, n.maxIntegrity);
      if (n.integrity <= 0 && n.type !== 'home') loseNode(state, n);
      // A Home at 0 integrity is left owned so clocks.ts detects conquest this same tick.
      continue;
    }

    // Neutral crater — only a drone can seed here.
    const youW = coreSeeders(state, 'you', n);
    const botW = coreSeeders(state, 'bot', n);
    const youWar = coreWarriorDps(state, 'you', n);
    const botWar = coreWarriorDps(state, 'bot', n);
    const youCan = youW > 0 && botW === 0 && botWar === 0;
    const botCan = botW > 0 && youW === 0 && youWar === 0;
    n.contested = (youW > 0 && botW > 0) || (youW > 0 && botWar > 0) || (botW > 0 && youWar > 0);

    // HONEST BAR: the seed channel only advances while the colony can PAY for it (the free Home
    // seed always can). If a drone is parked on a crater the side can't afford, the bar doesn't
    // fill — it drains — and we surface WHY (throttled toast), so it never looks silently broken.
    const advance = (side: Side): void => {
      if (canSeed(state, side, n)) {
        n.claimProgress = Math.min(CAPTURE.establishSeconds, n.claimProgress + dt);
        if (n.claimProgress >= CAPTURE.establishSeconds) {
          chargeSeed(state, side, n);
          establishNode(state, n, side);
        }
      } else {
        n.claimProgress = Math.max(0, n.claimProgress - dt * 2);
        if (side === 'you') emitSeedBlocked(state, n, dt); // tell the player they need resources
      }
    };

    if (youCan) advance('you');
    else if (botCan) advance('bot');
    else n.claimProgress = Math.max(0, n.claimProgress - dt * 2);
  }
}
